import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { defer, finalize, from, Observable, tap } from 'rxjs';
import { ExternalToast, toast } from 'ngx-sonner';

export enum NotificationType {
  DEFAULT = 'default',
  DANGER = 'danger',
  SUCCESS = 'success',
  WARNING = 'warning',
  INFO = 'info',
}

export interface NotifyOptions {
  /** Paramètres d'interpolation de la clé i18n. */
  params?: Record<string, unknown>;
  /** Clé i18n (ou texte déjà prêt) affichée sous le titre. */
  description?: string;
  /** Bouton d'action — ex. « Annuler ». `label` est une clé i18n. */
  action?: { label: string; onClick: () => void };
  /** Identifiant de dédoublonnage. Par défaut : clé + params. */
  id?: string;
  duration?: number;
}

export interface PromiseToastKeys {
  loading: string;
  success: string;
  error: string;
}

type ToastKind = 'success' | 'error' | 'warning' | 'info' | 'default';

/**
 * Toast « collant » : reste affiché jusqu'à fermeture.
 * PAS `Infinity` : quand un toast est mis à jour (même id — dédoublonnage, passage
 * chargement → erreur d'un `promise()`), ngx-sonner relance `setTimeout(…, duration)`
 * sans tester l'infini, et le navigateur ramène un délai > 2³¹-1 ms à 0 : le toast
 * se fermerait aussitôt. 2³¹-1 ms (~24 jours) est le plus grand délai honoré.
 */
const STICKY = 2_147_483_647;

const DURATIONS: Record<ToastKind, number> = {
  success: 4000,
  info: 4000,
  default: 4000,
  warning: 6000,
  // L'agent doit pouvoir lire — voire recopier — un message d'erreur : pas de fermeture auto.
  error: STICKY,
};

const KIND_BY_TYPE: Record<NotificationType, ToastKind> = {
  [NotificationType.DEFAULT]: 'default',
  [NotificationType.DANGER]: 'error',
  [NotificationType.SUCCESS]: 'success',
  [NotificationType.WARNING]: 'warning',
  [NotificationType.INFO]: 'info',
};

/** Délai laissé à l'appelant pour afficher sa propre erreur avant le toast de repli. */
const FALLBACK_DELAY_MS = 300;

/**
 * Seule porte d'entrée vers les toasts. Aucun composant n'importe `ngx-sonner`
 * directement : la lib reste remplaçable sans toucher aux appelants.
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly translateService = inject(TranslateService);
  /** Replis en attente → leur description (le `detail` backend), reprise par l'appelant. */
  private readonly pendingFallbacks = new Map<ReturnType<typeof setTimeout>, string | undefined>();
  private promiseCounter = 0;

  success(key: string, opts?: NotifyOptions): void {
    this.show('success', key, opts);
  }

  error(key: string, opts?: NotifyOptions): void {
    this.show('error', key, opts);
  }

  warning(key: string, opts?: NotifyOptions): void {
    this.show('warning', key, opts);
  }

  info(key: string, opts?: NotifyOptions): void {
    this.show('info', key, opts);
  }

  /**
   * Un seul toast pour une action longue : « chargement » puis succès ou erreur.
   * Le toast appartient à `promise()` — l'appelant n'affiche rien lui-même, sinon doublon.
   * L'Observable renvoyé est froid : rien ne part tant que l'appelant ne souscrit pas.
   */
  promise<T>(
    work: Observable<T> | Promise<T>,
    keys: PromiseToastKeys,
    opts?: NotifyOptions,
  ): Observable<T> {
    return defer(() => {
      // Id chaîne généré ici : ngx-sonner compare les id en `===`, et l'id numérique qu'il
      // génère seul ne serait plus reconnu une fois passé dans `NotifyOptions.id` (string).
      const id = opts?.id ?? `promise-${++this.promiseCounter}`;
      toast.loading(this.translate(keys.loading, opts?.params), { id, duration: STICKY });
      const toastOpts = { ...opts, id };
      let settled = false;
      const settle = (fn: () => void) => {
        if (!settled) {
          settled = true;
          fn();
        }
      };
      return from(work).pipe(
        tap({
          next: () => settle(() => this.show('success', keys.success, toastOpts)),
          complete: () => settle(() => this.show('success', keys.success, toastOpts)),
          error: () => settle(() => this.show('error', keys.error, toastOpts)),
        }),
        // Désabonnement avant toute réponse (navigation, composant détruit) : on retire le spinner.
        finalize(() => settle(() => toast.dismiss(id))),
      );
    });
  }

  /**
   * Erreur de repli, pour les intercepteurs globaux : ne s'affiche que si aucun appelant
   * n'a montré sa propre erreur entre-temps. Évite le double toast (message technique
   * global + message métier de l'appelant) sans laisser muette une erreur non gérée.
   *
   * Si l'appelant affiche sa propre erreur, il récupère la `description` du repli (le
   * `detail` backend) quand il n'en fournit pas : titre métier + cause, en un seul toast.
   *
   * ⚠️ L'annulation est globale : toute erreur affichée, ou `acknowledgeError()`, annule
   * TOUS les replis en attente — y compris celui d'un échec concurrent sans rapport. Assumé :
   * deux échecs dans la même fenêtre de 300 ms sont presque toujours la même panne.
   */
  errorFallback(key: string, opts?: NotifyOptions): void {
    const handle = setTimeout(() => {
      this.pendingFallbacks.delete(handle);
      this.show('error', key, opts);
    }, FALLBACK_DELAY_MS);
    this.pendingFallbacks.set(handle, opts?.description);
  }

  /**
   * L'erreur est signalée autrement qu'en toast (bandeau `grid-load-error`) ou ignorée
   * volontairement (`catchError` silencieux) : annule le repli en attente, sans rien afficher.
   */
  acknowledgeError(): void {
    this.cancelFallbacks();
  }

  dismiss(id?: string): void {
    toast.dismiss(id);
  }

  /**
   * @deprecated Utiliser `success()`, `error()`, `warning()` ou `info()`, qui acceptent
   * des paramètres i18n. `width` est ignoré : la largeur est fixée par la charte.
   */
  showNotification(type: NotificationType, title: string, text?: string, _width?: string): void {
    this.show(KIND_BY_TYPE[type], title, { description: text });
  }

  private show(kind: ToastKind, key: string, opts: NotifyOptions = {}): void {
    // Le `detail` backend repris d'un repli annulé reste brut : c'est un texte serveur, pas une
    // clé, et il peut contenir des `{{ … }}` que l'interpolation i18n ne doit pas toucher.
    const carriedDetail = kind === 'error' ? this.cancelFallbacks() : undefined;
    const description = opts.description
      ? this.translate(opts.description, opts.params)
      : carriedDetail;
    const data: ExternalToast = {
      id: opts.id ?? this.dedupeId(key, opts.params),
      description,
      // Toujours explicite : sur une mise à jour, ngx-sonner fusionne l'ancien toast et le
      // nouveau — une durée omise hériterait de celle du toast remplacé (ex. « chargement »).
      duration: opts.duration ?? DURATIONS[kind],
      // Lecteurs d'écran : une erreur interrompt (aria-live assertive), le reste attend.
      important: kind === 'error',
      action: opts.action && {
        label: this.translate(opts.action.label),
        onClick: () => opts.action?.onClick(),
      },
    };
    const title = this.translate(key, opts.params);
    if (kind === 'default') {
      toast(title, data);
    } else {
      toast[kind](title, data);
    }
  }

  /** Annule les replis en attente ; renvoie la description du plus récent qui en portait une. */
  private cancelFallbacks(): string | undefined {
    let lastDescription: string | undefined;
    this.pendingFallbacks.forEach((description, handle) => {
      clearTimeout(handle);
      lastDescription = description ?? lastDescription;
    });
    this.pendingFallbacks.clear();
    return lastDescription;
  }

  /** Même message répété = même toast, rafraîchi au lieu d'empilé. */
  private dedupeId(key: string, params?: Record<string, unknown>): string {
    return params ? `${key}|${JSON.stringify(params)}` : key;
  }

  private translate(text: string | undefined, params?: Record<string, unknown>): string {
    // `instant` lève « Parameter "key" required » sur une clé vide : un titre calculé
    // (`detail ?? …`) ne doit pas faire planter l'affichage d'une erreur.
    if (!text) return '';
    // `instant` renvoie le texte tel quel s'il ne correspond à aucune clé (ex. `detail` backend).
    return this.translateService.instant(text, params);
  }
}
